import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { Dispatch, SetStateAction, useContext, useState } from "react";
import { CustomEvent } from "schemas/functions";

import TemplateEditButton from "components/TemplateEditButton";
import { MAXIMUM_FOLLOWUP_ALLOWED } from "constants/templates";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import EditTemplateDialog from "features/Influencer/Templates/TemplatesTable/EditTemplate";
import { makeDeepCopy } from "utils/updateLocalState";

import styles from "../styles";
import CreateTemplateSetUp from "./CreateTemplateSetUp";

interface Props {
  setDefaultFollowUps: Dispatch<SetStateAction<Template[]>>;
  defaultOutreach?: Template;
  defaultFollowUps: Template[];
  setDefaultOutreach: Dispatch<SetStateAction<Template | undefined>>;
}

const DefaultTemplateSetUp = ({
  defaultOutreach,
  defaultFollowUps,
  setDefaultOutreach,
  setDefaultFollowUps,
}: Props) => {
  const {
    outreachTemplates,
    followupTemplates,
    editTemplateDialogId,
    setEditTemplateDialogId,
  } = useContext(OutreachTemplatesContext);

  const defaultTemplate = {
    subject: "",
    contentType: TemplateContentType.OUTREACH,
    body: "",
    name: "Outreach Template",
  };

  const [createTemplateOpen, setCreateTemplateOpen] =
    useState<TemplateContentType | null>(null);
  const [editTemplate, setEditTemplate] = useState<Template>(defaultTemplate);

  const handleDeleteFollowUp = (index: number) => {
    const updatedFollowUps = makeDeepCopy(defaultFollowUps);
    updatedFollowUps.splice(index, 1);
    setDefaultFollowUps(updatedFollowUps);
  };

  const onOutreachChange = (id: number) => {
    const template = outreachTemplates?.find((t) => t.id === id);
    if (template) {
      setDefaultOutreach(template);
    } else {
      setDefaultOutreach(undefined);
    }
  };

  const onFollowUpChange = (id: number, index: number) => {
    if (id === -1) {
      setCreateTemplateOpen(TemplateContentType.FOLLOWUP);
      setEditTemplate({
        subject: "",
        contentType: TemplateContentType.FOLLOWUP,
        body: "",
        name: `Follow-Up Template ${index + 1}`,
      });
      return;
    }
    const template = followupTemplates?.find((t) => t.id === id);
    setDefaultFollowUps((prevFollowUps) => {
      const updatedFollowUps = [...prevFollowUps];
      if (template) {
        updatedFollowUps[index] = template;
      }
      return updatedFollowUps;
    });
  };

  const handleAddFollowUp = () => {
    if (followupTemplates?.length === 0) {
      setCreateTemplateOpen(TemplateContentType.FOLLOWUP);
      setEditTemplate({
        subject: "",
        contentType: TemplateContentType.FOLLOWUP,
        body: "",
        name: `Follow-Up Template ${defaultFollowUps.length + 1}`,
      });
      return;
    }
    if (defaultFollowUps.length >= MAXIMUM_FOLLOWUP_ALLOWED) {
      return;
    }

    const copy = makeDeepCopy(defaultFollowUps);
    copy.push(followupTemplates[0]);
    setDefaultFollowUps(copy);
  };

  return (
    <>
      <Typography textAlign="center" gutterBottom>
        {outreachTemplates?.length === 0
          ? "Click the button below to create your first template."
          : ""}
      </Typography>
      <FormHelperText sx={{ mb: 4 }}>
        Your template should be generic enough to work for{" "}
        <strong>ALL niches</strong>. We use this template for both the niches
        you selected as well as any brands of any niche that you save to be
        pitched to.
      </FormHelperText>

      <Grid container alignItems="center" sx={{ mb: 1 }} gap={2}>
        <Grid item xs={2}>
          <strong>Outreach</strong>
        </Grid>
        <Grid item xs>
          {outreachTemplates?.length > 0 ? (
            <FormControl fullWidth>
              <Select
                name={TemplateContentType.OUTREACH}
                sx={styles.textField}
                value={defaultOutreach?.id || ""}
                onChange={(e: CustomEvent) => onOutreachChange(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">
                  <em>Select Outreach</em>
                </MenuItem>
                {outreachTemplates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Button
              variant="contained"
              disableElevation
              onClick={() =>
                setCreateTemplateOpen(TemplateContentType.OUTREACH)
              }
            >
              Create Template ✨
            </Button>
          )}
        </Grid>

        <Grid item xs={1}>
          {defaultOutreach?.id && (
            <TemplateEditButton templateId={defaultOutreach?.id} />
          )}
        </Grid>
      </Grid>

      {defaultFollowUps?.length > 0 &&
        defaultFollowUps?.map((defaultFollowUp, index) => (
          <Grid container alignItems="center" gap={2} sx={{ mb: 1 }}>
            <Grid item xs={2}>
              <strong>Follow-Up {index + 1}</strong>
            </Grid>
            <Grid item xs>
              <FormControl fullWidth>
                <Select
                  displayEmpty
                  name={TemplateContentType.FOLLOWUP}
                  sx={styles.textField}
                  value={defaultFollowUp?.id || ""}
                  onChange={(e: CustomEvent) =>
                    onFollowUpChange(e.target.value, index)
                  }
                >
                  {followupTemplates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
                  <MenuItem value={-1} sx={{ color: "green.main" }}>
                    <em>Create a New Follow-Up </em>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid container item xs={1}>
              {defaultFollowUp?.id && (
                <TemplateEditButton templateId={defaultFollowUp?.id} />
              )}
              {defaultFollowUp?.id && (
                <Tooltip title="Delete follow-up">
                  <IconButton onClick={() => handleDeleteFollowUp(index)}>
                    <Box
                      sx={{ fontSize: 14 }}
                      component="i"
                      className="fa-regular fa-trash"
                    />
                  </IconButton>
                </Tooltip>
              )}
            </Grid>
          </Grid>
        ))}

      {defaultFollowUps?.length < MAXIMUM_FOLLOWUP_ALLOWED && (
        <Grid container alignItems="center" gap={2}>
          <Grid item xs={2}></Grid>
          <Grid item xs>
            <Button
              sx={{ fontSize: 12, textTransform: "none" }}
              size="small"
              onClick={handleAddFollowUp}
              disabled={outreachTemplates?.length === 0}
            >
              Add Follow Up +
            </Button>
          </Grid>
        </Grid>
      )}

      {editTemplateDialogId && (
        <EditTemplateDialog
          propsTemplateId={editTemplateDialogId}
          propsHandleClose={() => {
            setEditTemplateDialogId(null);
          }}
        />
      )}

      <CreateTemplateSetUp
        open={Boolean(createTemplateOpen)}
        handleClose={() => {
          setCreateTemplateOpen(null);
          setEditTemplate(defaultTemplate);
        }}
        editTemplate={editTemplate}
        setEditTemplate={setEditTemplate}
      />
    </>
  );
};

export default DefaultTemplateSetUp;
